import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { Store } from "../../../../api";

import styles from "./StoreHomePage.module.scss";
import {
  getCategoriesResponse,
  getImageUrl,
  getProductsResponse,
  getSettingsResponse,
  pickRandomProducts,
} from "./StoreHomePage.form";

const NOVELTIES_POOL_SIZE = 40;

export function StoreHomePage() {
  const location = useLocation();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [heroBannerPath, setHeroBannerPath] = useState("");
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);

  useEffect(() => {
    async function loadSettings() {
      try {
        const storeController = new Store();
        const result = await storeController.getSettings();

        setHeroBannerPath(getSettingsResponse(result).heroBannerPath);
      } catch (error) {
        console.error("StoreHomePage loadSettings error:", error);

        setHeroBannerPath("");
      }
    }

    loadSettings();
  }, []);

  useEffect(() => {
    async function loadCategories() {
      try {
        setIsLoadingCategories(true);

        const storeController = new Store();
        const result = await storeController.getCategories();

        setCategories(getCategoriesResponse(result));
      } catch (error) {
        console.error("StoreHomePage loadCategories error:", error);

        setCategories([]);
      } finally {
        setIsLoadingCategories(false);
      }
    }

    async function loadProducts() {
      try {
        setIsLoadingProducts(true);

        const storeController = new Store();

        const featuredResult = await storeController.getProducts(
          1,
          NOVELTIES_POOL_SIZE,
          { featured: "true", availability: "inStock" }
        );

        const featuredDocs = getProductsResponse(featuredResult);

        if (featuredDocs.length > 0) {
          setProducts(pickRandomProducts(featuredDocs, 8));

          return;
        }

        const recentResult = await storeController.getProducts(
          1,
          NOVELTIES_POOL_SIZE,
          { sort: "recent", availability: "inStock" }
        );

        setProducts(pickRandomProducts(getProductsResponse(recentResult), 8));
      } catch (error) {
        console.error("StoreHomePage loadProducts error:", error);

        setProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    }

    loadCategories();
    loadProducts();
  }, []);

  return (
    <div className={styles.StoreHomePage}>
      <section className={styles.Hero}>
        {heroBannerPath ? (
          // Imagen real en flujo normal (no de fondo/recortada): el
          // panel toma exactamente el alto que esta imagen necesita a
          // su ancho completo, sin recortar ni deformar nada de la foto.
          <img
            className={styles.HeroBackdrop}
            src={getImageUrl(heroBannerPath)}
            alt=""
            aria-hidden="true"
          />
        ) : (
          <div className={styles.HeroBackdropFallback} aria-hidden="true" />
        )}

        <div className={styles.HeroOverlay} aria-hidden="true" />

        <div className={styles.HeroContent}>
          <p className={styles.HeroEyebrow}>Lux &amp; Glam</p>

          <h1>Piezas con carácter, para cada ocasión</h1>

          <p className={styles.HeroSubtitle}>
            Explora nuestro catálogo y encuentra la pieza que buscas.
          </p>

          <Link to="/catalogo" className={styles.HeroButton}>
            Ver catálogo
          </Link>
        </div>
      </section>

      <section className={styles.Section}>
        <div className={styles.SectionHeader}>
          <h2>Categorías</h2>
        </div>

        {isLoadingCategories ? (
          <div className={styles.StateMessage}>Cargando categorías...</div>
        ) : categories.length === 0 ? (
          <div className={styles.StateMessage}>
            Todavía no hay categorías disponibles.
          </div>
        ) : (
          <div className={styles.CategoryGrid}>
            {categories.map((category) => (
              <Link
                key={category._id}
                to={`/catalogo/${category.slug}`}
                className={styles.CategoryCard}
              >
                {category.image?.path ? (
                  <img
                    src={getImageUrl(category.image.path)}
                    alt={category.name}
                    className={styles.CategoryImage}
                  />
                ) : (
                  <span className={styles.CategoryImagePlaceholder}>
                    {category.name?.charAt(0) || "?"}
                  </span>
                )}

                <span className={styles.CategoryName}>{category.name}</span>

                <span className={styles.CategoryCount}>
                  {category.productsCount}{" "}
                  {category.productsCount === 1 ? "producto" : "productos"}
                </span>

                <span className={styles.CategoryCardHint}>
                  Ver productos <span aria-hidden="true">→</span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className={styles.Section}>
        <div className={styles.SectionHeader}>
          <h2>Novedades</h2>

          <Link to="/catalogo" className={styles.SectionLink}>
            Ver todo →
          </Link>
        </div>

        {isLoadingProducts ? (
          <div className={styles.StateMessage}>Cargando productos...</div>
        ) : products.length === 0 ? (
          <div className={styles.StateMessage}>
            Todavía no hay productos disponibles.
          </div>
        ) : (
          <div className={styles.ProductGrid}>
            {products.map((product) => (
              <Link
                key={product._id}
                to={`/producto/${product.slug}`}
                state={{ from: `${location.pathname}${location.search}` }}
                className={styles.ProductCard}
              >
                <span className={styles.ProductImageWrapper}>
                  {product.image ? (
                    <img
                      src={getImageUrl(product.image)}
                      alt={product.name}
                      className={styles.ProductImage}
                    />
                  ) : (
                    <span className={styles.ProductImagePlaceholder}>▣</span>
                  )}

                  {!product.inStock && (
                    <span className={styles.OutOfStockBadge}>Agotado</span>
                  )}
                </span>

                {product.brand && (
                  <span className={styles.ProductBrand}>{product.brand}</span>
                )}

                <span className={styles.ProductName}>{product.name}</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
